import { Country } from './Country';
import { Language } from './Language';
import { Region } from './Region';
import { Site, siteIDToLegacySite } from './Site';
import type { SiteID as ModernSiteID } from './SiteID';

/**
 * Represents the schema to which the narrower type literal `regionCountriesMap`
 * must adhere.
 * @see {@link regionCountriesMap}
 */
type RegionCountriesMap = { readonly [key in Region]: ReadonlyArray<Country> };

/**
 * This object specifies all valid countries for each region.
 *
 * The purpose of this object is both to support type checking that only valid
 * region-country combinations are used in the application, and to provide more
 * useful Intellisense in TypeScript-supported IDEs.
 */
export const regionCountriesMap = {
  AFRICA: [],
  NA: [Country.US, Country.CA],
  EMEA: [Country.GB],
  EUROPE: [],
  ME: [],
  APAC: [],
  LATAM: []
} as const satisfies RegionCountriesMap;

/**
 * Represents the schema to which the narrower type literal
 * `countryLanguagesMap` must adhere.
 * @see {@link countryLanguagesMap}
 */
type CountryLanguagesMap = {
  // Keys must be valid members of the `Country` enum, and values must either be
  // an object representing a country or an alias thereof.
  readonly [key in Country]:  //
    | { readonly languages: ReadonlyArray<Language> }
    | { readonly aliasFor: keyof CountryLanguagesMap };
};

/**
 * This object specifies all valid languages for each country that we support.
 *
 * The purpose of this object is both to support type checking that only valid
 * {@link ILocale locales} (language-country combinations) are used in the
 * application, and to provide more useful Intellisense in TypeScript-supported
 * IDEs.
 */
export const countryLanguagesMap = {
  [Country.AD]: { languages: [Language.EN] },
  [Country.AL]: { languages: [Language.EN] },
  [Country.AT]: { languages: [Language.EN] },
  [Country.AU]: { languages: [Language.EN] },
  [Country.BA]: { languages: [Language.EN] },
  [Country.BE]: { languages: [Language.EN] },
  [Country.BG]: { languages: [Language.EN] },
  [Country.BR]: { languages: [Language.EN] },
  [Country.CA]: { languages: [Language.EN, Language.FR] },
  [Country.CL]: { languages: [Language.EN] },
  [Country.CN]: { languages: [Language.EN] },
  [Country.CO]: { languages: [Language.EN] },
  [Country.CY]: { languages: [Language.EN] },
  [Country.CZ]: { languages: [Language.EN] },
  [Country.DK]: { languages: [Language.EN] },
  [Country.DZ]: { languages: [Language.EN] },
  [Country.EE]: { languages: [Language.EN] },
  [Country.ES]: { languages: [Language.EN] },
  [Country.FI]: { languages: [Language.EN] },
  [Country.FR]: { languages: [Language.FR] },
  [Country.GB]: { languages: [Language.EN] },
  [Country.DE]: { languages: [Language.DE] },
  [Country.GR]: { languages: [Language.EN] },
  [Country.HK]: { languages: [Language.EN] },
  [Country.HR]: { languages: [Language.EN] },
  [Country.HU]: { languages: [Language.EN] },
  [Country.ID]: { languages: [Language.EN] },
  [Country.IE]: { languages: [Language.EN] },
  [Country.IL]: { languages: [Language.EN] },
  [Country.IS]: { languages: [Language.EN] },
  [Country.IT]: { languages: [Language.IT] },
  [Country.JE]: { languages: [Language.EN] },
  [Country.JP]: { languages: [Language.JA] },
  [Country.KR]: { languages: [Language.EN] },
  [Country.KZ]: { languages: [Language.EN] },
  [Country.LV]: { languages: [Language.EN] },
  [Country.LT]: { languages: [Language.EN] },
  [Country.LU]: { languages: [Language.EN] },
  [Country.MT]: { languages: [Language.EN] },
  [Country.MA]: { languages: [Language.EN] },
  [Country.MC]: { languages: [Language.EN] },
  [Country.MD]: { languages: [Language.EN] },
  [Country.ME]: { languages: [Language.EN] },
  [Country.MK]: { languages: [Language.EN] },
  [Country.NL]: { languages: [Language.NL] },
  [Country.NO]: { languages: [Language.EN] },
  [Country.NZ]: { languages: [Language.EN] },
  [Country.PH]: { languages: [Language.EN] },
  [Country.PL]: { languages: [Language.EN] },
  [Country.PT]: { languages: [Language.EN] },
  [Country.RE]: { languages: [Language.EN] },
  [Country.RO]: { languages: [Language.EN] },
  [Country.RU]: { languages: [Language.EN] },
  [Country.RS]: { languages: [Language.EN] },
  [Country.SE]: { languages: [Language.EN] },
  [Country.SG]: { languages: [Language.EN] },
  [Country.SK]: { languages: [Language.EN] },
  [Country.SI]: { languages: [Language.EN] },
  [Country.CH]: { languages: [Language.EN] },
  [Country.TN]: { languages: [Language.EN] },
  [Country.TR]: { languages: [Language.EN] },
  [Country.TW]: { languages: [Language.EN] },
  [Country.AE]: { languages: [Language.EN] },
  [Country.UK]: { aliasFor: Country.GB },
  [Country.US]: { languages: [Language.EN] },
  [Country.ZA]: { languages: [Language.EN] }
} as const satisfies CountryLanguagesMap;

/**
 * Determines whether the provided language is supported in the provided country.
 * @param language - The language to check.
 * @param country - The country to check.
 * @returns `true` if the language is supported in the country; otherwise, `false`.
 */
export function isLanguageSupportedInCountry(
  language: Language,
  country: Country
): boolean {
  let jumps = 0;
  let supportedLanguages = countryLanguagesMap[country] as
    | CountryLanguagesMap[Country]
    | undefined;

  while (supportedLanguages && 'aliasFor' in supportedLanguages) {
    if (++jumps > 5) {
      console.warn(`Possible circular alias for ${country}.`);
      return false;
    }

    supportedLanguages = countryLanguagesMap[supportedLanguages.aliasFor] as
      | CountryLanguagesMap[Country]
      | undefined;
  }

  if (!supportedLanguages) {
    console.warn(`${country} is not configured with supported languages.`);
    return false;
  }

  const { languages } = supportedLanguages as {
    readonly languages: ReadonlyArray<Language>;
  };

  return languages.includes(language);
}

/**
 * Represents the schema to which the narrower type literal `siteCountriesMap`
 * must adhere.
 *
 * @see {@link siteCountriesMap}
 */
type SiteCountriesMap = {
  // Keys must be a be valid, known site. Values must be a tuple of valid
  // locales for that site.
  readonly [key in Site]: ReadonlyArray<Country>;
};

/**
 * This object represents all valid countries for each of our sites.
 *
 * The purpose of this object is both to support type checking that only valid
 * site-country combinations are used in the application, and to provide more
 * useful Intellisense in TypeScript-supported IDEs.
 */
export const siteCountriesMap = {
  SANUK: [Country.US, Country.GB],
  UGG: [Country.US, Country.CA, Country.UK],
  'UGG-COLLAB': [Country.US],
  TEVA: [Country.US],
  HOKA: [
    Country.US, Country.CA,
    Country.BR, Country.CL, Country.CO,
    Country.DZ, Country.MA, Country.RE, Country.TN, Country.ZA,
    Country.GB,
    Country.AL, Country.AD, Country.AT, Country.BA, Country.BE, Country.BG,
    Country.HR, Country.CY, Country.CZ, Country.DK, Country.EE, Country.FI,
    Country.FR, Country.DE, Country.GR, Country.HU, Country.IS, Country.IE,
    Country.IT, Country.JE, Country.LV, Country.LT, Country.LU, Country.MT,
    Country.MC, Country.MD, Country.ME, Country.NL, Country.MK,
    Country.NO, Country.PL, Country.PT, Country.RO, Country.RS, Country.SK,
    Country.SI, Country.ES, Country.SE, Country.CH, Country.KZ,
    Country.IL, Country.AE, Country.TR,
    Country.AU, Country.CN, Country.ID, Country.JP, Country.NZ, Country.PH
  ],
  KOOLABURRA: [Country.US],
  AHNU: [Country.US],
  DECKERSX: [Country.US],
  CONDUCTOR: [Country.US]
} as const satisfies SiteCountriesMap;

/**
 * Determines whether the provided country is supported in the provided site.
 * @param site - The site to check.
 * @param country - The country to check.
 * @returns `true` if the country is supported in the site; otherwise, `false`.
 */
export function isCountrySupportedInSite(
  site: ModernSiteID,
  country: Country
): boolean {
  const legacySite = siteIDToLegacySite(site);
  const countriesForSite = (siteCountriesMap as SiteCountriesMap)[legacySite];
  return countriesForSite.includes(country);
}

/**
 * The default language for the application.
 *
 * This is the primary language used when no other language is specified or
 * detected.
 */
export const DEFAULT_LANGUAGE = Language.EN;

/**
 * The default country for the application.
 *
 * This is the primary country/region used when no other country is specified or
 * detected.
 */
export const DEFAULT_COUNTRY = Country.US;

/**
 * The default locale for the application, derived from DEFAULT_LANGUAGE and
 * DEFAULT_COUNTRY.
 *
 * This locale is used throughout the codebase as the primary/canonical locale
 * that does not require a URL prefix in routes or explicit locale handling in
 * various contexts (sitemaps, middleware, etc.).
 *
 * When multi-locale support expands beyond US English, update DEFAULT_LANGUAGE
 * and DEFAULT_COUNTRY and this constant will automatically reflect the change.
 */
export const DEFAULT_LOCALE: ConfiguredLocaleString = `${DEFAULT_LANGUAGE}-${DEFAULT_COUNTRY}`;

/**
 * Represents _any_ valid Locale, expressed as an [IETF language
 * tag](https://en.wikipedia.org/wiki/IETF_language_tag).
 *
 * NOTE: Not all strings of this type are usable on our sites.
 *
 * @see {@link ConfiguredLocaleString}.
 *
 * @example `en-US`
 */
export type LocaleString = `${Language}-${Country}`;

/**
 * Represents valid and configured Locales for our sites, expressed as [IETF
 * language tags](https://en.wikipedia.org/wiki/IETF_language_tag).
 *
 * @example 'en-US'
 *
 * @see {@link LocaleString} if you need to type-check a locale string that is
 * not necessarily configured for our sites.
 */
export type ConfiguredLocaleString = LocaleStringForSite<Site>;

/**
 * Describes a Locale: a set of parameters that defines the user's language,
 * region and any special variant preferences that the user wants to see in
 * their user interface.
 *
 * DECA Locale objects can be converted to and assembled from [IETF language
 * tags](https://en.wikipedia.org/wiki/IETF_language_tag).
 *
 * @see https://en.wikipedia.org/wiki/Locale_(computer_software)
 */
export interface ILocale {
  /**
   * Represents a ISO 639-1 language identifier.
   * @example 'en'
   */
  readonly language: Language;

  /**
   * Represent a ISO 3166-1 alpha-2 country code identifier.
   * @example 'US'
   */
  readonly country: Country;

  /**
   * A `.toString()` implementation for the locale object to convert to a
   * {@link ConfiguredLocaleString}.
   *
   * @returns The locale as a string.
   * @example 'en-US'
   */
  toString(): ConfiguredLocaleString;
}

/**
 * An object that represents a site that has been adapted (localized) for a
 * specific {@link ILocale locale} (i.e. a specific country and language).
 */
export interface ILocalizedSite {
  /** The unique identifier for this site as adapted. */
  id: string;

  /** The unique identifier of the {@link Region} this site is for. */
  regionID: Region;

  /** The unique identifier of the {@link Country} this site is for. */
  countryID: Country;

  /** The locale this site is being adapted for. */
  locale: ILocale;

  /**
   * A map of translated names for the country this site is being adapted for,
   * indexed by {@link Language language}.
   *
   * @example
   * ```ts
   * // For country "US":
   *
   * aliases: {
   *    [Language.EN]: 'United States',
   *    [Language.DE]: 'Vereinigte Staaten',
   *    [Language.ES]: 'Estados Unidos'
   * }
   * ```
   */
  aliases: LanguageNameMap;

  /** The URL of the site as adapted for the country and language. */
  url: URL;
}

/**
 * Utility type to get a tuple of valid language strings for a given country.
 *
 * Tuple will be a conceptual array of valid ISO 639-1 language identifiers.
 *
 * @see {@link countryLanguagesMap}
 */
export type AllowedLanguagesForCountry<
  // Represents an object for any given key in the `countryLanguagesMap` type
  // literal.
  Country extends Mappings[keyof Mappings],
  // Represents an alias for the `countryLanguagesMap` type literal.
  Mappings = typeof countryLanguagesMap
> =
  // If the `Country` represents an alias for another country, resolve that
  // country.
  Country extends { aliasFor: any }
    ? ResolveAliasForCountry<Country, Mappings>
    : // Otherwise, get the languages for this country.
      Country extends { languages: ReadonlyArray<Language> }
      ? Country['languages']
      : never;

/**
 * Utility type to resolve to the actual underlying country for a given country
 * alias. For example mapping `UK` to `GB` (United Kingdom to Great Britain).
 */
export type ResolveAliasForCountry<
  // Represents an object for any given key in the `countryLanguagesMap` type
  // literal.
  Country extends { aliasFor: keyof Mappings },
  // Represents an alias for the `countryLanguagesMap` type literal.
  Mappings = typeof countryLanguagesMap
> = Mappings[Country['aliasFor']] extends { languages: ReadonlyArray<Language> }
  ? Mappings[Country['aliasFor']]['languages']
  : never;

/**
 * Utility type to get a tuple of valid country strings for a given site. Tuple
 * will be a conceptual array of valid ISO 3166-1 alpha-2 country code
 * identifiers.
 * @see {@link siteCountriesMap}
 */
export type AllowedCountriesForBrand<
  // Represents a key in the `siteCountriesMap` type literal.
  Site extends keyof Mappings,
  // Represents an alias for the `siteCountriesMap` type literal.
  Mappings = typeof siteCountriesMap
> = Mappings[Site];

/**
 * Represents the valid {@link ConfiguredLocaleString locale strings} for a
 * given site in the format of `${Language}-${Country}`.
 *
 * @see {@link siteCountriesMap}
 * @see {@link countryLanguagesMap}
 */
export type LocaleStringForSite<
  // Represents a key in the `siteCountriesMap` type literal.
  Site extends keyof typeof siteCountriesMap,
  // Represents a key in the `countryLanguagesMap` type literal.
  CountryLanguagesMap = typeof countryLanguagesMap,
  // Represents all of the valid locales for a site as specified in
  // `siteCountriesMap`.
  Locales extends ReadonlyArray<
    keyof CountryLanguagesMap
    // If the valid locales for the given site are valid keys of
    // `countryLanguagesMap`, use those.
  > = AllowedCountriesForBrand<Site> extends ReadonlyArray<
    keyof CountryLanguagesMap
  >
    ? AllowedCountriesForBrand<Site>
    : never
  // For the valid locales for the given site, get the lang locale strings.
> = `${LocaleStringForCountry<Locales, CountryLanguagesMap>}`;

/**
 * Utility type to get a union of valid locale strings for a given country.
 *
 * @see {@link countryLanguagesMap}
 * @see https://en.wikipedia.org/wiki/IETF_language_tag
 */
export type LocaleStringForCountry<
  // Represents a tuple type of keys of the `countryLanguagesMap` type literal.
  Country extends ReadonlyArray<keyof CountryLanguagesMap>,
  // Represents an alias for the `countryLanguagesMap` type literal.
  CountryLanguagesMap = typeof countryLanguagesMap
> = {
  // For every index position in the `Country` tuple (a constant array of keys
  // of the `countryLanguagesMap` type literal), produce a lang locale string.
  // Prove that the indexes are numbers for this type mapping (For instance,
  // don't count `.length`).
  [Index in keyof Country]: Country[Index] extends Country[number]
    ? // Get the allowed languages for the country key at this index.
      `${`${AllowedLanguagesForCountry<
        CountryLanguagesMap[Country[Index]],
        CountryLanguagesMap
        // Resolve the locale string given the country key at this index.
      >[number]}-${LocaleForCountry<Country[Index], CountryLanguagesMap>}`}`
    : never;
}[number];

/**
 * Utility type which will resolve a locale string from a key of the type
 * literal `countryLanguagesMap`.
 *
 * @see {@link countryLanguagesMap}
 * @see https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
 */
export type LocaleForCountry<
  // Represents a tuple type of keys of the `countryLanguagesMap` type literal.
  Country extends keyof CountryLanguagesMap,
  // Represents an alias for the `countryLanguagesMap` type literal.
  CountryLanguagesMap = typeof countryLanguagesMap
> = `${`${Country extends string
  ? // If `Country` is a string and is the key to an object that has a
    // `languages` property, output this `Country`.
    CountryLanguagesMap[Country] extends { languages: ReadonlyArray<Language> }
    ? Country
    : // Else if, `Country` is a string and is the key to an object that has an
      // `aliasFor` property, use that alias key instead of the `Country`.
      CountryLanguagesMap[Country] extends { aliasFor: any }
      ? `${CountryLanguagesMap[Country]['aliasFor']}`
      : never
  : never}`}`;

/**
 * A type that represents a combination of a site and a country in the format of
 * `${Site}-${Country}`.
 */
export type SiteCountry = `${Site}-${Country}`;

/**
 * @deprecated Use `SiteID` from the `constructs` lib instead.
 *
 * Describes a valid site identifier.
 *
 * For now, site IDs are only expressed as `${Site}-${Country}`. Making them
 * equivalent to {@link SiteCountry}. This may change in the future.
 */
export type SiteID = SiteCountry;

/**
 * The metadata for a country, including its region and aliases in different
 * languages.
 */
export type LanguageNameMap = { [key in Language]?: string };

/**
 * Describes a map with metadata for the configured {@link Region Regions} in
 * the sites. Keys are members of the {@link Region} enum.
 */
export type RegionMetadata = {
  [key in Region]?: {
    /**
     * A map of translated names for the region, indexed by {@link Language language}.
     *
     * @example
     * ```ts
     * // For country "NA":
     *
     * aliases: {
     *    [Language.EN]: 'North America',
     *    [Language.DE]: 'Nordamerika',
     *    [Language.ES]: 'América del Norte'
     * }
     * ```
     */
    aliases: LanguageNameMap;
  };
};

/**
 * Describes a map with metadata for the configured {@link Region Countries} in
 * the sites. Keys are members of the {@link Country} enum.
 */
export type CountryMetadata = {
  [key in Country]?: {
    /** The ID of the {@link Country} this country belongs to. */
    regionID: Region;

    /**
     * A map of translated names for the country, indexed by {@link Language language}.
     *
     * @example
     * ```ts
     * // For country "US":
     *
     * aliases: {
     *    [Language.EN]: 'United States',
     *    [Language.DE]: 'Vereinigte Staaten',
     *    [Language.ES]: 'Estados Unidos'
     * }
     * ```
     */
    aliases: { [key in Language]?: string };
  };
};

/**
 * Describes a map with settings for a {@link Language language} when used in
 * a site along with a Country.
 *
 * Keys are members of the {@link Language} enum.
 *
 * @see {@link CountrySettingsForSite}
 */
export type LanguageSettingsForCountry = {
  [key in Language]?: {
    enabled: boolean;

    /**
     * The URL of the site when localized for the current language and country.
     *
     * If unspecified, a URL will be auto-generated at runtime according to the
     * current site's localization mechanisms.
     */
    url?: string;
  };
};

/**
 * Describes a map with settings for a {@link Country country} when used in
 * a site.
 *
 * Keys are members of the {@link Country} enum.
 */
export type CountrySettingsForSite = {
  [key in Country]?: {
    enabled: boolean;

    /**
     * The languages the site is available on when adapted to the country this
     * localization represents.
     */
    languages: LanguageSettingsForCountry;

    /**
     * The language that will be selected by default when the site is adapted to
     * the country this localization represents.
     */
    defaultLanguage: Language;

    /**
     * The ID of the {@link Region} this country belongs to.
     */
    regionID: Region;
  };
};

/**
 * Describes a structure that contains all the configured localized sites, in
 * the following hierarchy:.
 *
 * ```
 * Region
 * |- Country
 *    |- Localized Site
 * ```
 */
export type LocalizedSiteTree = {
  [key in Region]?: RegionMetadata & {
    countries: {
      [key in Country]?: CountryMetadata & {
        languages: { [key in Language]?: ILocalizedSite };
      };
    };
  };
};

/**
 * Describes an value that can be either a {@link ConfiguredLocaleString configured Locale String} or a
 * {@link Language} code.
 */
export type LanguageOrLocale = ConfiguredLocaleString | Language;

/**
 * Describes a structure that contains localized values for a specific
 * attribute.
 *
 * Keys are either {@link LanguageOrLocale locale or language} identifiers, or
 * `'default'` to indicate default values. Default values are always required.
 *
 * @example
 * ```ts
 * const mockCategory: ICategory = {
 *  name: {
 *    default: 'Mock Category',
 *    fr: 'Catégorie Simulée'
 *  },
 *  description: {
 *    default: 'This is a mock category for testing purposes',
 *    fr: 'Ceci est une catégorie simulée à des fins de test'
 *  }
 * }
 *  ```
 */
export type LocalizedValues<T> = { [key in LanguageOrLocale]?: T } & {
  default: T;
};

/**
 * Given a either a {@link LocalizedValues} object, or an object containing
 * `LocalizedValues` members, deeply collapse these values into their underlying
 * types.
 *
 * Use this type when creating a struct that's adapted for a specific locale
 * from another, pre-existing struct.
 *
 * Non-object types will be passed through unmodified.
 *
 * @example
 * ```ts
 * type Item = {
 *  name: LocalizedValues<string>;
 *  isAvailable: LocalizedValues<boolean>;
 *  price: LocalizedValues<number>;
 * }
 *
 * type LocalizedItem = LocaleSpecificValue<Item>;
 *
 * type LocalizedItemEquivalent = {
 *  name: string;
 *  isAvailable: boolean;
 *  price: number;
 * }
 * ```
 */
export type LocaleSpecificValue<T> =
  // If the type provided is a set of localized values, unwrap the type and
  // recurse on it.
  T extends LocalizedValues<infer V>
    ? LocaleSpecificValue<V>
    : // If the type provided is an object that is NOT a set of localized
      // values, recurse on each value.
      T extends object
      ? { [Key in keyof T]: LocaleSpecificValue<T[Key]> }
      : // Otherwise, just return the type as-is.
        T;
